import MemberAvatar from "@/components/members/member-avatar";
import ColorAvatar from "@/components/members/color-avatar";
import DefaultLayout from "@/layouts/default-layout";
import { getUserInitials } from "@/utils";
import { getColorForId } from "@/utils/avatars";
import { useSession, useUser } from "@clerk/react";
import { Button, Card, Popover } from "@heroui/react";
import { List, User, UserMinus, UserPlus, Users } from "lucide-react";
import React, { useEffect, useState } from "react";
import {
  getRecentConnections as getRecentConnectionsApi,
  getFrequentConnections as getFrequentConnectionsApi,
  getMyConnections as getMyConnectionsApi,
} from "@/api/users";
import {
  getRecentMeetsActivity as getRecentMeetsActivityApi,
  getMyMeetsUpComing as getMyMeetsUpComingApi,
} from "@/api/meets";
import { getActivity as getActivityApi, getRecentActivity as getRecentActivityApi } from "@/api/activity";
import { useNavigate } from "react-router-dom";
import CreateMemberDrawer from "@/components/drawers/create-member-drawer";
import { cn } from "tailwind-variants";
import PulledDownWrapper from "@/components/wrappers/pulled-down-wrapper";

const popoverActionClass = "justify-start items-center gap-2.5 rounded-xl px-3 h-10 font-medium text-sm text-left";

const MAX_VISIBLE_CONNECTIONS = 10;
const SKELETON_RING_COUNT = 4;

const RING_CONTAINER_SIZE = 156;
const RING_RADIUS = RING_CONTAINER_SIZE / 2;

function getPositionOnCircle(index, total, radius) {
  const angle = (index / total) * 360 - 90;
  const radians = (angle * Math.PI) / 180;

  return {
    x: radius * Math.cos(radians),
    y: radius * Math.sin(radians),
  };
}

const skeletonRingPositions = Array.from({ length: SKELETON_RING_COUNT }, (_, i) =>
  getPositionOnCircle(i, SKELETON_RING_COUNT, RING_RADIUS),
);

function formatRelativeTime(isoString) {
  if (!isoString) return "";

  const diffMs = Math.max(Date.now() - new Date(isoString).getTime(), 0);
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 1) return "now";
  if (diffMinutes < 60) return `${diffMinutes}m`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d`;
}

// --- Skeletons -----------------------------------------------------------

const AvatarSkeleton = ({ className = "size-9" }) => (
  <div className={`rounded-full bg-default/30 dark:bg-white/20 animate-pulse ${className}`} />
);

const ActivityCardSkeleton = () => (
  <Card className="border-white dark:border-white/40 border-0 bg-white/40 dark:bg-white/10 flex-row justify-between items-center p-2 rounded-2xl">
    <div className="flex items-center gap-2 flex-1">
      <div className="size-9 rounded-full bg-default/30 dark:bg-white/20 animate-pulse shrink-0" />
      <div className="flex flex-col gap-1.5 flex-1">
        <div className="h-3 w-24 rounded-full bg-default/30 dark:bg-white/20 animate-pulse" />
        <div className="h-2.5 w-40 rounded-full bg-default/20 dark:bg-white/20 animate-pulse" />
      </div>
    </div>
    <div className="h-3 w-6 rounded-full bg-default/20 dark:bg-white/20 animate-pulse shrink-0" />
  </Card>
);

// --- Activity card --------------------------------------------------------

const EVENT_TYPE_VERB = {
  joined_meet: "joined",
  left_meet: "left",
  created_meet: "created",
  updated_meet: "updated",
  recorded_meet: "recorded",
  added_to_meet: "was added to",
  removed_from_meet: "was removed from",
  changed_email: "changed their email",
  changed_name: "changed their name",
  uploaded_audio: "uploaded audio for",
};

function getEventVerb(eventType) {
  return EVENT_TYPE_VERB[eventType] ?? eventType?.replace(/_/g, " ") ?? "did something";
}

const ActivityCard = ({ event, onViewClick }) => {
  const actorName = event.created_by?.user_fullname ?? "Someone";
  const actorId = event.created_by?.user_id;
  const actorColor = getColorForId(event.created_by?.user_id);
  const verb = getEventVerb(event.event_type);
  const subject = event.event_value ?? "";

  return (
    <Popover>
      <Popover.Trigger>
        <Card className="activity-card shadow-none bg-white/40 dark:bg-white/10 flex-row justify-between items-center p-3 px-3 text-sm rounded-[16px]">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <ColorAvatar id={event.created_by?.user_id} name={actorName} color={actorColor} />

            <p className="text-foreground dark:text-white text-sm leading-snug">
              <span className="font-bold">{actorName}</span>
              <span className="font-normal text-foreground/70 dark:text-white/60"> {verb} </span>
              {subject && <span className="font-bold text-accent">{subject}</span>}
            </p>
          </div>

          <span className="text-[#A09EC0] font-medium text-sm shrink-0 ml-3">
            {formatRelativeTime(event.created_on)}
          </span>
        </Card>
      </Popover.Trigger>

      <Popover.Content>
        <Popover.Dialog className="flex flex-col gap-1 min-w-[200px] p-1">
          <Button variant="ghost" className={popoverActionClass} onPress={() => onViewClick(actorId)}>
            <User className="size-4" />
            View {actorName}
          </Button>
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
};

const NetworkPage = () => {
  const { user, isLoaded: isUserLoaded } = useUser();
  const { session } = useSession();
  const navigate = useNavigate();
  const [view, setView] = useState("network");
  const [openCreateMemberDrawer, setOpenCreateMemberDrawer] = useState();

  const [connections, setConnections] = useState([]);
  const [frequentConnections, setFrequentConnections] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [upcomingMeets, setUpcomingMeets] = useState([]);

  const [isLoadingConnections, setIsLoadingConnections] = useState(false);
  const [isLoadingFrequentConnections, setIsLoadingFrequentConnections] = useState(false);
  const [isLoadingActivity, setIsLoadingActivity] = useState(false);
  const [isLoadingUpcoming, setIsLoadingUpcoming] = useState(false);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    if (!isUserLoaded || !user || !session) return;

    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserLoaded, user, session]);

  const getData = async () => {
    await Promise.all([
      getConnections(),
      getFrequentConnections(),
      getRecentActivity(),
      getMyMeetsUpComing(),
    ]);
  };

  const getConnections = async () => {
    setIsLoadingConnections(true);
    setFetchError(false);
    const token = await session.getToken();

    return getMyConnectionsApi(token)
      .then((_resp) => {
        if (_resp.data.Result === "OK") {
          setConnections(_resp.data.Records);
        } else {
          throw new Error("Failed to load connections");
        }
      })
      .catch((err) => {
        console.log(err);
        setFetchError(true);
      })
      .finally(() => {
        setIsLoadingConnections(false);
      });
  };

  const getMyMeetsUpComing = async () => {
    setIsLoadingUpcoming(true);
    const token = await session.getToken();

    return getMyMeetsUpComingApi(token)
      .then((_resp) => {
        if (_resp.data.Result === "OK") {
          setUpcomingMeets(_resp.data.Records);
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setIsLoadingUpcoming(false);
      });
  };

  const getFrequentConnections = async () => {
    setIsLoadingFrequentConnections(true);
    const token = await session.getToken();

    return getFrequentConnectionsApi(user.id, token)
      .then((_resp) => {
        if (_resp.data.Result === "OK") {
          setFrequentConnections(_resp.data.Records);
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoadingFrequentConnections(false);
      });
  };

  const getRecentActivity = async () => {
    setIsLoadingActivity(true);
    const token = await session.getToken();

    const today = new Date().toISOString().split("T")[0];
    const dateRange = [today, today];

    return getRecentActivityApi(user.id, dateRange, token)
      .then((_resp) => {
        if (_resp.data.Result === "OK") {
          setRecentActivity(_resp.data.Records);
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoadingActivity(false);
      });
  };

  const toggleView = () => {
    if (view === "network") {
      setView("list");
    } else {
      setView("network");
    }
  };

  const visibleConnections = frequentConnections.slice(0, MAX_VISIBLE_CONNECTIONS);
  const overflowConnectionsCount = frequentConnections.length - visibleConnections.length;

  const isRingLoading = !isUserLoaded || isLoadingFrequentConnections;
  const ringSpinClass = isRingLoading ? "" : "animate-spin [animation-duration:30s]";
  const counterSpinClass = isRingLoading ? "" : "animate-spin [animation-duration:30s] [animation-direction:reverse]";

  return (
    <>
      <PulledDownWrapper onRefresh={getData}>
        <div className="flex flex-col gap-8 mt-24">
          <section className="flex flex-col text-glass-foreground gap-3">
            <div className="flex flex-col gap-2">
              <span className="text-[54px] font-bold leading-[54px]">Network</span>

              <div className="font-bold text-glass-foreground-muted text-xl">
                People in your <span className="text-glass-foreground">Loopp</span>
              </div>
            </div>
            <div
              className="relative py-2 px-4 pr-16 rounded-3xl flex justify-between items-center h-[78px]"
              style={{
                backgroundBlendMode: "plus-lighter, luminosity, darken",
              }}
            >
              <img src="/network-focus-dark.png" className="absolute rounded-3xl left-0 hidden dark:block" />
              <img src="/network-focus-light.png" className="absolute rounded-3xl left-0 dark:hidden" />
              <div className="flex items-center gap-2 relative z-2">
                <div className="size-10 flex justify-center items-center rounded-full bg-[#FFFFFF0F] mix-blend-plus-lighter">
                  <Users fill="white" color="white" />
                </div>

                <div className="flex flex-col gap-1 justify-center">
                  <span className="text-accent dark:text-glass-foreground-muted text-lg font-bold leading-5">
                    {isLoadingConnections ? (
                      <span className="inline-block h-4 w-6 rounded-full bg-default/30 dark:bg-white/20 animate-pulse align-middle" />
                    ) : (
                      connections.length
                    )}
                  </span>
                  <span className="text-glass-foreground dark:text-glass-foreground-muted font-medium leading-2">
                    People
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 relative z-2" onClick={() => navigate("/upcoming")}>
                <div className="size-10 flex justify-center items-center rounded-full bg-[#FFFFFF0F] mix-blend-plus-lighter">
                  <img src="/loops-icon.svg" />
                </div>

                <div className="flex flex-col gap-1">
                  <span className="text-accent dark:text-glass-foreground-muted text-lg font-bold leading-5">
                    {" "}
                    {isLoadingUpcoming ? (
                      <span className="inline-block h-4 w-6 rounded-full bg-default/30 dark:bg-white/20 animate-pulse align-middle" />
                    ) : (
                      upcomingMeets.length
                    )}
                  </span>
                  <span className="text-glass-foreground dark:text-glass-foreground-muted font-medium leading-2">
                    Loopps
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-1.5">
              <Button
                isIconOnly
                className={"rounded-xl glass-surface p-1"}
                onPress={() => setOpenCreateMemberDrawer(true)}
              >
                <img src="/users-plus.svg" />
              </Button>

              <Button
                className={`rounded-xl flex items-center glass-surface px-2", ${view === "list" && "bg-accent! text-white!"}`}
                onPress={toggleView}
                variant={view === "list" ? "primary" : "outline"}
              >
                {view === "network" ? (
                  <>
                    <img src="/network.svg" />

                    <span className="text-[11px] text-accent dark:text-[#BFBFBF] capitalize font-bold">network</span>
                  </>
                ) : (
                  <>
                    <List className="text-white size-4" />

                    <span className="text-[11px] text-white capitalize font-bold">List</span>
                  </>
                )}
              </Button>
            </div>
          </section>

          {fetchError ? (
            <section className="flex flex-col items-center gap-3 py-10 text-center text-glass-foreground">
              <p className="font-semibold">Something went wrong!</p>
              <p className="text-sm text-glass-foreground-muted">We couldn't find your connections.</p>
              <button className="text-sm underline" onClick={getConnections}>
                Try again
              </button>
            </section>
          ) : view === "network" ? (
            <section className="relative flex flex-col items-center justify-center">
              <div
                className={`flex relative items-center justify-center min-h-[156px] min-w-[156px] h-[156px] w-[156px] rounded-full ${ringSpinClass}`}
              >
                <div className="border border-black/30 dark:border-white/30 h-full w-full rounded-full flex items-center justify-center">
                  {isRingLoading
                    ? skeletonRingPositions.map(({ x, y }, i) => (
                        <div
                          key={`ring-skeleton-${i}`}
                          className="absolute"
                          style={{
                            left: `calc(50% + ${x}px)`,
                            top: `calc(50% + ${y}px)`,
                            transform: "translate(-50%, -50%)",
                          }}
                        >
                          <AvatarSkeleton />
                        </div>
                      ))
                    : visibleConnections.map((connection, i) => {
                        const { x, y } = getPositionOnCircle(i, visibleConnections.length, RING_RADIUS);

                        return (
                          <div
                            key={connection.id ?? connection._id ?? i}
                            className="absolute"
                            style={{
                              left: `calc(50% + ${x}px)`,
                              top: `calc(50% + ${y}px)`,
                              transform: "translate(-50%, -50%)",
                            }}
                          >
                            <div className={counterSpinClass}>
                              <MemberAvatar member={connection} color={connection.color} />
                            </div>
                          </div>
                        );
                      })}

                  <div className={`relative flex items-center justify-center ${counterSpinClass}`}>
                    {isRingLoading ? (
                      <AvatarSkeleton className="size-14" />
                    ) : (
                      <>
                        <div className={`${ringSpinClass} [animation-duration:3s]!`}>
                          <img src="/user-avatar-bg.svg" />
                        </div>
                        <span className="text-accent absolute text-sm font-bold tracking-wider">
                          {getUserInitials(user.fullName)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {!isRingLoading && overflowConnectionsCount > 0 && (
                <Button variant="ghost" className="absolute text-xs font-semibold">
                  +{overflowConnectionsCount} more
                </Button>
              )}
            </section>
          ) : (
            <section className="flex flex-col gap-2">
              {connections.map((connection, i) => {
                const color = getColorForId(connection.id);
                return (
                  <Popover key={connection.id ?? i}>
                    <Popover.Trigger>
                      <Card className="activity-card bg-white/40 shadow-none dark:bg-white/10 flex-row justify-between items-center p-2 px-3 text-sm rounded-[16px]">
                        <div className="flex items-center gap-2">
                          <ColorAvatar id={connection.id} name={connection.fullname} color={color} />
                          <span className="capitalize font-bold">{connection.fullname}</span>
                        </div>
                      </Card>
                    </Popover.Trigger>

                    <Popover.Content>
                      <Popover.Dialog className="flex flex-col gap-1 min-w-[200px] p-1">
                        <Button
                          variant="ghost"
                          className={popoverActionClass}
                          onPress={() => navigate(`/network/${connection.id}`)}
                        >
                          <User className="size-4" />
                          View {connection.fullname}
                        </Button>
                      </Popover.Dialog>
                    </Popover.Content>
                  </Popover>
                );
              })}
            </section>
          )}

          <div className="flex flex-col gap-3 pb-40">
            <span className="text-[13px] tracking-[-0.13px] font-bold">Recent Activity</span>
            <div className="flex flex-col gap-2">
              {isLoadingActivity ? (
                Array.from({ length: 3 }).map((_, i) => <ActivityCardSkeleton key={`activity-skeleton-${i}`} />)
              ) : recentActivity.length > 0 ? (
                recentActivity.map((event) => (
                  <ActivityCard
                    key={event._id}
                    event={event}
                    onViewClick={(userId) => navigate(`/network/${userId}`)}
                  />
                ))
              ) : (
                <span className="text-sm text-glass-foreground-muted">No recent activity yet.</span>
              )}
            </div>
          </div>
        </div>
      </PulledDownWrapper>
      <CreateMemberDrawer show={openCreateMemberDrawer} onResp={() => setOpenCreateMemberDrawer(false)} />
    </>
  );
};

export default NetworkPage;